import type { Breadcrumb, Event } from '@sentry/react-native';

const REDACTED = '[REDACTED]';
const SENSITIVE_KEYS = new Set([
  'password',
  'token',
  'accesstoken',
  'refreshtoken',
  'code',
  'clientsecret',
  'secret',
  'idtoken',
  'authorization',
  'email',
  'phone',
  'apikey',
]);

const isSensitive = (key: string): boolean => {
  const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
  return SENSITIVE_KEYS.has(normalized);
};

export const sanitizeValue = (key: string, value: unknown): unknown => {
  if (isSensitive(key)) {
    return REDACTED;
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(key, item));
  }

  if (value && typeof value === 'object') {
    return sanitizeObject(value as Record<string, unknown>);
  }

  return value;
};

export const sanitizeObject = (
  obj?: Record<string, unknown>,
): Record<string, unknown> | undefined => {
  if (!obj) {
    return undefined;
  }

  const sanitized: Record<string, unknown> = {};

  for (const key of Object.keys(obj)) {
    sanitized[key] = sanitizeValue(key, obj[key]);
  }

  return sanitized;
};

export const scrubEvent = <T extends Event>(event: T): T => {
  const newEvent = { ...event };

  if (newEvent.extra) {
    newEvent.extra = sanitizeObject(newEvent.extra);
  }

  if (newEvent.user) {
    newEvent.user = sanitizeObject(newEvent.user as Record<string, unknown>) as Event['user'];
  }

  if (newEvent.breadcrumbs) {
    newEvent.breadcrumbs = newEvent.breadcrumbs
      .map(scrubBreadcrumb)
      .filter((b): b is Breadcrumb => b !== null);
  }

  if (newEvent.contexts) {
    newEvent.contexts = sanitizeObject(
      newEvent.contexts as Record<string, unknown>,
    ) as Event['contexts'];
  }

  if (newEvent.request) {
    newEvent.request = { ...newEvent.request };

    if (newEvent.request.headers) {
      newEvent.request.headers = sanitizeObject(
        newEvent.request.headers as Record<string, unknown>,
      ) as Record<string, string>;
    }

    if (newEvent.request.cookies) {
      newEvent.request.cookies = sanitizeObject(
        newEvent.request.cookies as Record<string, unknown>,
      ) as Record<string, string>;
    }

    if (newEvent.request.data) {
      newEvent.request.data = sanitizeValue('data', newEvent.request.data);
    }
  }

  return newEvent;
};

export const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
  if (!breadcrumb.data) {
    return breadcrumb;
  }

  const newBreadcrumb = { ...breadcrumb };
  newBreadcrumb.data = sanitizeObject(newBreadcrumb.data as Record<string, unknown>);
  return newBreadcrumb;
};

export const beforeBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
  if (breadcrumb.category === 'http') {
    return null;
  }

  return scrubBreadcrumb(breadcrumb);
};
